import { useEffect, useMemo, useState } from 'react'
import { PHISHING_DIFFICULTY, PHISHING_PAYLOAD_TYPE } from '../../domain/domainCodes'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Fish } from 'lucide-react'
import FileField from '../../components/FileField'
import FormPage from '../../components/FormPage'
import useGoBack from '../../components/useGoBack'
import { FormCols, FormSection, InfoNote, SwitchField } from '../../components/Form'
import FormRow from '../../components/FormRow'
import Select from '../../components/Select'
import TextField from '../../components/TextField'
import { useToast } from '../../components/toastContext'
import { getTemplate, getTemplatesPage, saveTemplate } from './phishingService'
import { templatesStrings as S } from './strings'
import TemplateSurfaceEditor from './TemplateSurfaceEditor'
import { serverText } from '../../lib/i18n'

const EMPTY = {
  categoryId: '',
  name: '',
  difficulty: String(PHISHING_DIFFICULTY.EASY),
  payloadType: String(PHISHING_PAYLOAD_TYPE.WEBSITE),
  isArabic: 'true',
  isActive: true,

  subject: '',
  emailImage: null,
  body: '',
  attachments: null,
  trackingImage: false,

  landingPage: '',
  landingPageImage: null,
  captureSubmittedData: false,
  capturePasswords: false,

  redirectToUrl: false,
  afterPhishingPageImage: null,
  redirectUrl: '',
  afterPage: '',
}

const asOptions = (map) => Object.entries(map).map(([value, label]) => ({ value, label }))

/**
 * قالب إصطياد — Views/PhishingTemplates/Index.cshtml.
 *
 * A four-step wizard in the legacy, and the redesign had only its first step:
 * the six identity fields. Everything a phishing template actually IS was
 * missing — the mail, the page the link lands on, and what happens after
 * someone is caught.
 *
 *   1  the template   category, name, difficulty, payload type, language, active
 *   2  the email      subject, thumbnail, body, attachments, tracking pixel
 *   3  the landing    the page behind the link, and what it captures
 *   4  after          the redirect, or the page shown once caught
 *
 * The steps are the campaign's own chronology — compose, land, resolve — which
 * is why they read as a sequence rather than as four tabs of settings, and why
 * a step's controls only appear once the one before it is answered.
 */
export default function TemplateFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  /* Back to the page this form was opened from — the list as it was left,
     not its first page. See components/useGoBack.js. */
  const goBack = useGoBack('/phishing/templates')
  /* Handed over by the list, so the ordinary edit costs no request at all. */
  const handedOver = useLocation().state?.template
  const toast = useToast()
  const [values, setValues] = useState(EMPTY)
  const [errors, setErrors] = useState({})
  const [categories, setCategories] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true

    getTemplatesPage()
      .then((page) => alive && setCategories(page.categories))
      .catch(() => alive && setCategories([]))

    if (id && handedOver?.id === Number(id)) {
      setValues({ ...EMPTY, ...handedOver })
    } else if (id) {
      getTemplate(id)
        .then((row) => {
          if (!alive) return
          /* A template that did not load leaves the wizard on its defaults, which looks
             like a blank new one — and saving that would add a duplicate rather than edit
             anything, because the API creates whenever the id is not a positive number. */
          if (!row) return toast.error(S.actionFailed)
          setValues({ ...EMPTY, ...row })
        })
        .catch((error) => alive && toast.error(error?.message || S.actionFailed))
    }

    return () => {
      alive = false
    }
  }, [id, handedOver, toast])

  const set = (key) => (value) => {
    setValues((previous) => ({ ...previous, [key]: value }))
    setErrors((previous) => ({ ...previous, [key]: undefined }))
  }

  const steps = useMemo(
    () => [
      { id: 'identity', label: S.form.steps.identity },
      { id: 'email', label: S.form.steps.email },
      { id: 'landing', label: S.form.steps.landing },
      { id: 'after', label: S.form.steps.after },
    ],
    [],
  )

  /* The legacy marks category, name, subject, body, landing page and — only
     when the redirect is on — the URL and the post-catch page as required. */
  const validateStep = (index) => {
    const next = {}
    if (index === 0) {
      if (!values.categoryId) next.categoryId = S.form.required
      if (!values.name.trim()) next.name = S.form.required
    }
    if (index === 1) {
      if (!values.subject.trim()) next.subject = S.form.required
      if (!values.body.trim()) next.body = S.form.required
    }
    if (index === 2 && !values.landingPage.trim()) next.landingPage = S.form.required
    if (index === 3 && values.redirectToUrl && !values.redirectUrl.trim())
      next.redirectUrl = S.form.required
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const submit = async () => {
    setSaving(true)
    const outcome = await saveTemplate({ ...values, id })
    setSaving(false)
    /* The save answers `{Result:false, Message}` with a 200 for a duplicate name or a
       rejected image, so the outcome is what decides — not the status code. */
    if (!outcome.ok) return toast.error(outcome.message || S.actionFailed)
    toast.success(serverText(outcome.message) || S.form.saved)
    navigate('/phishing/templates')
  }

  return (
    <FormPage
      icon={Fish}
      title={S.form.title}
      subtitle={S.title}
      steps={steps}
      validateStep={validateStep}
      onBack={goBack}
      onSubmit={submit}
      submitLabel={S.form.save}
      submitting={saving}
    >
      {({ step }) => {
        if (step === 0) {
          return (
            <FormSection>
              <FormCols>
                <FormRow label={S.form.category} error={errors.categoryId}>
                  {(fid) => (
                    <Select
                      id={fid}
                      value={values.categoryId}
                      onChange={set('categoryId')}
                      placeholder={S.form.categoryPlaceholder}
                      invalid={!!errors.categoryId}
                      /* ALREADY `{value, label}`. This re-mapped them to
                         `{value: row.id, label: row.name}` — both undefined — so the select
                         drew seven blank rows, wrote the STRING "undefined" into
                         `categoryId` (truthy, so the required check passed), and on an edit
                         matched no option, silently dropping the template's real category
                         on the next save. */
                      options={categories}
                    />
                  )}
                </FormRow>
                <FormRow label={S.columns.name} error={errors.name}>
                  {(fid) => (
                    <TextField
                      id={fid}
                      value={values.name}
                      onChange={set('name')}
                      invalid={!!errors.name}
                      autoComplete="off"
                    />
                  )}
                </FormRow>
                <FormRow label={S.columns.difficulty}>
                  {(fid) => (
                    <Select
                      id={fid}
                      value={values.difficulty}
                      onChange={set('difficulty')}
                      options={asOptions(S.difficulties)}
                    />
                  )}
                </FormRow>
                <FormRow label={S.columns.payloadType}>
                  {(fid) => (
                    <Select
                      id={fid}
                      value={values.payloadType}
                      onChange={set('payloadType')}
                      options={asOptions(S.payloads)}
                    />
                  )}
                </FormRow>
                <FormRow label={S.columns.language}>
                  {(fid) => (
                    <Select
                      id={fid}
                      value={values.isArabic}
                      onChange={set('isArabic')}
                      options={[
                        { value: 'true', label: S.languages.arFull },
                        { value: 'false', label: S.languages.enFull },
                      ]}
                    />
                  )}
                </FormRow>
              </FormCols>

              <SwitchField
                checked={values.isActive}
                onChange={set('isActive')}
                label={S.form.isActive}
              />
            </FormSection>
          )
        }

        if (step === 1) {
          return (
            <FormSection>
              <FormCols>
                <FormRow label={S.form.subject} error={errors.subject}>
                  {(fid) => (
                    <TextField
                      id={fid}
                      value={values.subject}
                      onChange={set('subject')}
                      invalid={!!errors.subject}
                      autoComplete="off"
                    />
                  )}
                </FormRow>
                <FormRow label={S.form.thumbnail}>
                  {(fid) => (
                    <FileField id={fid} accept=".png" onChange={set('emailImage')} />
                  )}
                </FormRow>
              </FormCols>

              <FormRow label={`${S.form.body} (${S.form.richText})`} error={errors.body}>
                {(fid) => (
                  <TemplateSurfaceEditor
                    id={fid}
                    value={values.body}
                    onChange={set('body')}
                  />
                )}
              </FormRow>

              <FormCols>
                <FormRow label={S.form.attachments}>
                  {(fid) => <FileField id={fid} onChange={set('attachments')} />}
                </FormRow>
              </FormCols>

              <SwitchField
                checked={values.trackingImage}
                onChange={set('trackingImage')}
                label={S.form.trackingImage}
              />
            </FormSection>
          )
        }

        if (step === 2) {
          return (
            <FormSection>
              <FormRow
                label={`${S.form.landingPage} (${S.form.richText})`}
                error={errors.landingPage}
              >
                {(fid) => (
                  <TemplateSurfaceEditor
                    id={fid}
                    value={values.landingPage}
                    onChange={set('landingPage')}
                  />
                )}
              </FormRow>

              <FormCols>
                <FormRow label={S.form.thumbnail}>
                  {(fid) => (
                    <FileField id={fid} accept=".png" onChange={set('landingPageImage')} />
                  )}
                </FormRow>
              </FormCols>

              <SwitchField
                checked={values.captureSubmittedData}
                onChange={set('captureSubmittedData')}
                label={S.form.captureData}
              />
              <SwitchField
                checked={values.capturePasswords}
                onChange={set('capturePasswords')}
                label={S.form.capturePasswords}
              />
            </FormSection>
          )
        }

        return (
          <FormSection>
            {/* One switch decides which of two mutually exclusive things this
                step is. The legacy renders both and hides one in script; here
                the choice governs what is on screen, so there is never a URL
                field and a page editor asking to be filled at the same time. */}
            <SwitchField
              checked={values.redirectToUrl}
              onChange={set('redirectToUrl')}
              label={S.form.redirectToUrl}
            />

            {values.redirectToUrl ? (
              <FormCols>
                <FormRow label={S.form.redirectUrl} error={errors.redirectUrl}>
                  {(fid) => (
                    <TextField
                      id={fid}
                      dir="ltr"
                      value={values.redirectUrl}
                      onChange={set('redirectUrl')}
                      invalid={!!errors.redirectUrl}
                      autoComplete="off"
                    />
                  )}
                </FormRow>
              </FormCols>
            ) : (
              <>
                <FormRow label={`${S.form.redirectPage} (${S.form.richText})`}>
                  {(fid) => (
                    <TemplateSurfaceEditor
                      id={fid}
                      value={values.afterPage}
                      onChange={set('afterPage')}
                    />
                  )}
                </FormRow>
                <FormCols>
                  <FormRow label={S.form.thumbnail}>
                    {(fid) => (
                      <FileField
                        id={fid}
                        accept=".png"
                        onChange={set('afterPhishingPageImage')}
                      />
                    )}
                  </FormRow>
                </FormCols>
              </>
            )}

            <InfoNote>
              <bdi>{S.form.stepHints.after}</bdi>
            </InfoNote>
          </FormSection>
        )
      }}
    </FormPage>
  )
}
